import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { GoogleLogin } from '@react-oauth/google';
import { isGoogleOAuthConfigured } from '../config/googleOAuth';
import { isLinkedInOAuthConfigured, linkedinClientId, linkedinRedirectUri, linkedinScopes } from '../config/linkedinOAuth';
import RocketLoader from '../components/RocketLoader';

/* ── Google SVG logo ── */
const GoogleLogo = () => (
  <svg width="18" height="18" viewBox="0 0 48 48">
    <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
    <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
    <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
    <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.18 1.48-4.97 2.31-8.16 2.31-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
    <path fill="none" d="M0 0h48v48H0z"/>
  </svg>
);

const LinkedInLogo = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
    <path fill="#0A66C2" d="M20.45 20.45h-3.56v-5.57c0-1.33-.02-3.04-1.85-3.04-1.86 0-2.14 1.45-2.14 2.95v5.66H9.34V9h3.41v1.56h.05c.47-.9 1.64-1.85 3.37-1.85 3.61 0 4.28 2.38 4.28 5.47v6.27zM5.32 7.43a2.06 2.06 0 1 1 0-4.12 2.06 2.06 0 0 1 0 4.12zM7.1 20.45H3.54V9H7.1v11.45zM22.22 0H1.77C.79 0 0 .77 0 1.72v20.56C0 23.23.79 24 1.77 24h20.45c.98 0 1.78-.77 1.78-1.72V1.72C24 .77 23.2 0 22.22 0z"/>
  </svg>
);

/* ── Terms agreement checkbox ── */
const TermsCheckbox = ({ checked, onChange }) => (
  <div style={{ display:'flex', alignItems:'flex-start', gap:10, marginBottom:16 }}>
    <input
      type="checkbox"
      id="terms-agree"
      checked={checked}
      onChange={e => onChange(e.target.checked)}
      style={{ marginTop:2, width:16, height:16, accentColor:'#EF1D28', cursor:'pointer', flexShrink:0 }}
    />
    <label htmlFor="terms-agree" style={{ fontSize:12.5, color:'#374151', lineHeight:1.5, cursor:'pointer' }}>
      I have read and agree to MarsResume&apos;s{' '}
      <a href="/terms-of-use" target="_blank" rel="noopener noreferrer" style={{ color:'#EF1D28', fontWeight:600, textDecoration:'none' }}>Terms of use</a>,{' '}
      <a href="/privacy-policy" target="_blank" rel="noopener noreferrer" style={{ color:'#EF1D28', fontWeight:600, textDecoration:'none' }}>Privacy Policy</a>{' '}
      and{' '}
      <a href="/cookie-policy" target="_blank" rel="noopener noreferrer" style={{ color:'#EF1D28', fontWeight:600, textDecoration:'none' }}>Cookie Policy</a>
    </label>
  </div>
);

/* ── icons (inline SVG so no extra dep) ── */
const EyeIcon = ({ off }) => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    {off ? (
      <>
        <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94" />
        <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19" />
        <line x1="1" y1="1" x2="23" y2="23" />
      </>
    ) : (
      <>
        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
        <circle cx="12" cy="12" r="3" />
      </>
    )}
  </svg>
);

const CheckIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

const ShieldIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
  </svg>
);

/* ── animations injected once ── */
const AuthStyles = () => {
  useEffect(() => {
    const id = 'auth-styles';
    if (document.getElementById(id)) return;
    const s = document.createElement('style');
    s.id = id;
    s.textContent = `
      @keyframes authFadeUp { from { opacity:0; transform:translateY(20px); } to { opacity:1; transform:translateY(0); } }
      @keyframes authBlob1  { 0%,100% { transform:translate(0,0) scale(1); } 50% { transform:translate(30px,20px) scale(1.1); } }
      @keyframes authBlob2  { 0%,100% { transform:translate(0,0) scale(1); } 50% { transform:translate(-20px,30px) scale(0.9); } }
      @keyframes authBlob3  { 0%,100% { transform:translate(0,0) scale(1); } 50% { transform:translate(15px,-25px) scale(1.05); } }
      @keyframes authPulse  { 0%,100% { opacity:1; } 50% { opacity:0.6; } }
      @keyframes spin        { to { transform:rotate(360deg); } }
      .auth-inp { transition:border-color 0.18s ease, box-shadow 0.18s ease; }
      .auth-inp:focus { border-color:#EF1D28 !important; box-shadow:0 0 0 3px rgba(239,29,40,0.12) !important; }
      .auth-inp:hover:not(:focus):not(:disabled) { border-color:#D1D5DB !important; }
      .auth-inp:disabled { background:#F9FAFB !important; color:#9CA3AF !important; cursor:not-allowed; }
      .auth-btn { transition:all 0.18s ease; }
      .auth-btn:hover:not(:disabled) { transform:translateY(-1px); box-shadow:0 6px 20px rgba(239,29,40,0.35) !important; }
      .auth-btn:active:not(:disabled) { transform:translateY(0); }
      .auth-btn:disabled { opacity:0.7; cursor:not-allowed; }
      .auth-google:hover { background:#F9FAFB !important; border-color:#D1D5DB !important; }
      .auth-linkedin:hover { background:#F0F7FF !important; border-color:#0A66C2 !important; }
      .auth-otp-inp:focus { border-color:#EF1D28 !important; box-shadow:0 0 0 3px rgba(239,29,40,0.12) !important; background:#FFF7F7 !important; }
      .auth-card { animation: authFadeUp 0.45s ease both; }
      .auth-panel-left { display:none; }
      @media (min-width: 900px) { .auth-panel-left { display:flex !important; } }
      .auth-panel-dot {
        position:absolute; border-radius:50%; pointer-events:none;
      }
      .auth-panel-dot-1 { width:280px; height:280px; top:-80px; right:-80px;
        background:radial-gradient(circle, rgba(239,29,40,0.22) 0%, transparent 70%);
        animation:authBlob1 9s ease-in-out infinite; }
      .auth-panel-dot-2 { width:220px; height:220px; bottom:-60px; left:-50px;
        background:radial-gradient(circle, rgba(29,78,216,0.15) 0%, transparent 70%);
        animation:authBlob2 11s ease-in-out infinite; }
      .auth-panel-dot-3 { width:160px; height:160px; top:45%; left:55%;
        background:radial-gradient(circle, rgba(5,150,105,0.12) 0%, transparent 70%);
        animation:authBlob3 7s ease-in-out infinite; }
      .auth-panel-line {
        position:absolute; left:0; right:0; height:1px;
        background:linear-gradient(90deg,transparent,rgba(239,29,40,0.3),transparent);
        pointer-events:none;
      }
      .auth-feat-check {
        width:22px; height:22px; border-radius:50%; background:#EF1D28;
        display:flex; align-items:center; justify-content:center; flex-shrink:0;
        color:white; box-shadow:0 2px 8px rgba(239,29,40,0.35);
      }
      .auth-stat-pill {
        background:rgba(255,255,255,0.07); border:1px solid rgba(255,255,255,0.1);
        border-radius:10px; padding:10px 16px; flex:1; min-width:0;
        transition:background 0.2s;
      }
      .auth-stat-pill:hover { background:rgba(255,255,255,0.11); }
    `;
    document.head.appendChild(s);
  }, []);
  return null;
};

/* ── left brand panel ── */
const BrandPanel = ({ isSignup }) => (
  <div
    className="auth-panel-left"
    style={{
      flex: '0 0 44%',
      background: '#0A1120',
      padding: '52px 48px',
      flexDirection: 'column',
      justifyContent: 'center',
      position: 'relative',
      overflow: 'hidden',
      borderRight: '1px solid #1E2D45',
    }}
  >
    {/* animated blobs */}
    <div className="auth-panel-dot auth-panel-dot-1" />
    <div className="auth-panel-dot auth-panel-dot-2" />
    <div className="auth-panel-dot auth-panel-dot-3" />
    {/* subtle grid */}
    <div style={{ position:'absolute', inset:0, backgroundImage:'radial-gradient(rgba(255,255,255,0.04) 1px, transparent 1px)', backgroundSize:'28px 28px', pointerEvents:'none' }} />
    {/* horizontal accent line */}
    <div className="auth-panel-line" style={{ top:'30%' }} />

    <div style={{ position:'relative', zIndex:1 }}>
      {/* logo */}
      <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:52 }}>
        <div style={{ width:40, height:40, borderRadius:10, background:'linear-gradient(135deg,#EF1D28,#C81018)', display:'flex', alignItems:'center', justifyContent:'center', boxShadow:'0 4px 16px rgba(239,29,40,0.4)' }}>
          <img src="/assets/mars-logo.png" alt="MarsResume" style={{ width:26, height:26, objectFit:'contain' }} />
        </div>
        <div>
          <div style={{ fontSize:18, fontWeight:900, color:'#F1F5F9', letterSpacing:'-0.3px', lineHeight:1.1 }}>MarsResume</div>
          <div style={{ fontSize:10, color:'#64748B', fontWeight:400, lineHeight:1 }}>Resume That Takes You to Mars</div>
        </div>
      </div>

      <h2 style={{ color:'#F1F5F9', fontSize:30, fontWeight:900, lineHeight:1.2, margin:'0 0 14px', letterSpacing:'-0.6px' }}>
        {isSignup ? <>Build a Resume That<br /><span style={{ color:'#EF1D28' }}>Gets You Hired</span></> : <>Your Career Journey<br /><span style={{ color:'#EF1D28' }}>Starts Here</span></>}
      </h2>
      <p style={{ color:'#8B949E', fontSize:14, lineHeight:1.7, margin:'0 0 36px', maxWidth:320 }}>
        {isSignup
          ? 'AI-powered tools to craft, tailor, and score your resume for any job — in minutes.'
          : 'Join thousands of professionals who landed dream jobs with MarsResume.'}
      </p>

      {/* features */}
      {[
        'AI Resume Builder with 26+ templates',
        'ATS Score Analyzer & optimization tips',
        'One-click job tailoring',
        'Interview question generator',
        'Export to PDF',
      ].map(f => (
        <div key={f} className="auth-feature-item">
          <div className="auth-feat-check"><CheckIcon /></div>
          {f}
        </div>
      ))}

      {/* stats row */}
      <div style={{ marginTop:40, display:'flex', gap:10 }}>
        {[
          { value:'50K+', label:'Resumes Built' },
          { value:'94%', label:'ATS Pass Rate' },
          { value:'4.9★', label:'User Rating' },
        ].map(s => (
          <div key={s.value} className="auth-stat-pill">
            <div style={{ color:'#F1F5F9', fontSize:16, fontWeight:800, lineHeight:1.1 }}>{s.value}</div>
            <div style={{ color:'#64748B', fontSize:11, marginTop:2 }}>{s.label}</div>
          </div>
        ))}
      </div>

      {/* social proof avatars */}
      <div style={{ marginTop:20, display:'flex', alignItems:'center', gap:12, padding:'13px 16px', background:'rgba(239,29,40,0.08)', borderRadius:12, border:'1px solid rgba(239,29,40,0.18)' }}>
        <div style={{ display:'flex' }}>
          {['#4ADE80','#60A5FA','#FBBF24','#F472B6'].map((c,i) => (
            <div key={i} style={{ width:26, height:26, borderRadius:'50%', border:'2px solid #0A1120', background:c, marginLeft: i ? -8 : 0 }} />
          ))}
        </div>
        <div>
          <div style={{ color:'#F1F5F9', fontSize:13, fontWeight:700, lineHeight:1.2 }}>12,000+ professionals</div>
          <div style={{ color:'#64748B', fontSize:11 }}>already building with MarsResume</div>
        </div>
      </div>
    </div>
  </div>
);

/* ── shared input ── */
const Field = ({ label, type, value, onChange, placeholder, disabled, required, rightEl, autoFocus }) => (
  <div style={{ marginBottom:16 }}>
    <label style={{ display:'block', fontSize:13, fontWeight:600, color:'#374151', marginBottom:6 }}>{label}</label>
    <div style={{ position:'relative' }}>
      <input
        className="auth-inp"
        type={type}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        disabled={disabled}
        required={required}
        autoFocus={autoFocus}
        style={{
          width:'100%', padding:'11px 14px', paddingRight: rightEl ? 44 : 14,
          border:'1.5px solid #E5E7EB', borderRadius:8, fontSize:14,
          outline:'none', boxSizing:'border-box', transition:'border-color 0.2s, box-shadow 0.2s',
          fontFamily:'inherit', background:'white',
        }}
      />
      {rightEl && (
        <div style={{ position:'absolute', right:12, top:'50%', transform:'translateY(-50%)', color:'#9CA3AF', cursor:'pointer', display:'flex', alignItems:'center' }}>
          {rightEl}
        </div>
      )}
    </div>
  </div>
);

/* ── OTP boxes ── */
const OTPInput = ({ value, onChange }) => {
  const refs = [useRef(),useRef(),useRef(),useRef(),useRef(),useRef()];
  const digits = (value + '      ').slice(0,6).split('');

  const handleKey = (i, e) => {
    if (e.key === 'Backspace') {
      const next = digits.map((d,j) => j === i ? '' : d).join('').trimEnd();
      onChange(next);
      if (i > 0) refs[i-1].current?.focus();
    } else if (/^\d$/.test(e.key)) {
      const arr = [...digits];
      arr[i] = e.key;
      const next = arr.join('').replace(/ /g,'');
      onChange(next);
      if (i < 5) refs[i+1].current?.focus();
    }
  };

  const handlePaste = (e) => {
    const txt = e.clipboardData.getData('text').replace(/\D/g,'').slice(0,6);
    onChange(txt);
    refs[Math.min(txt.length, 5)].current?.focus();
    e.preventDefault();
  };

  return (
    <div style={{ display:'flex', gap:8, justifyContent:'center', margin:'4px 0 16px' }}>
      {refs.map((ref,i) => (
        <input
          key={i}
          ref={ref}
          className="auth-otp-inp"
          type="text"
          inputMode="numeric"
          maxLength={1}
          value={digits[i].trim()}
          onChange={() => {}}
          onKeyDown={e => handleKey(i, e)}
          onPaste={handlePaste}
          onFocus={e => e.target.select()}
          style={{
            width:44, height:52, textAlign:'center', fontSize:22, fontWeight:700,
            border:'1.5px solid #E5E7EB', borderRadius:10, outline:'none',
            transition:'border-color 0.2s, box-shadow 0.2s, background 0.2s',
            fontFamily:'inherit', background: digits[i].trim() ? '#FFF7F7' : 'white',
            color:'#111827',
          }}
        />
      ))}
    </div>
  );
};

/* ── divider ── */
const Divider = () => (
  <div style={{ display:'flex', alignItems:'center', gap:12, margin:'18px 0' }}>
    <div style={{ flex:1, height:1, background:'#E5E7EB' }} />
    <span style={{ fontSize:11, color:'#9CA3AF', fontWeight:600, letterSpacing:'0.05em' }}>OR</span>
    <div style={{ flex:1, height:1, background:'#E5E7EB' }} />
  </div>
);

/* ── google button wrapper ── */
const GoogleAuthButton = ({ onSuccess, onError, isSignup }) => {
  const [gLoading, setGLoading] = useState(false);

  if (isGoogleOAuthConfigured) {
    return (
      <div style={{ position:'relative', marginBottom:4 }}>
        <div style={{ opacity: gLoading ? 0.5 : 1, pointerEvents: gLoading ? 'none' : 'auto', display:'flex', justifyContent:'center' }}>
          <GoogleLogin
            onSuccess={cr => { setGLoading(false); onSuccess(cr); }}
            onError={() => { setGLoading(false); onError(); }}
            width="376"
            text={isSignup ? 'signup_with' : 'signin_with'}
            shape="rectangular"
            theme="outline"
          />
        </div>
      </div>
    );
  }

  return (
    <button
      type="button"
      className="auth-google"
      style={{
        width:'100%', padding:'11px 16px', background:'white', border:'1.5px solid #E5E7EB',
        borderRadius:8, fontSize:14, fontWeight:600, color:'#374151', cursor:'pointer',
        display:'flex', alignItems:'center', justifyContent:'center', gap:10,
        transition:'background 0.15s, border-color 0.15s', marginBottom:4,
      }}
      onClick={() => alert('Google sign-in is not configured. Please contact support.')}
    >
      <GoogleLogo />
      {isSignup ? 'Sign up with Google' : 'Sign in with Google'}
    </button>
  );
};

/* ── submit button ── */
const makeOAuthState = () => {
  if (window.crypto?.getRandomValues) {
    const bytes = new Uint8Array(16);
    window.crypto.getRandomValues(bytes);
    return Array.from(bytes, b => b.toString(16).padStart(2, '0')).join('');
  }
  return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
};

const LinkedInAuthButton = ({ onError, isSignup }) => {
  const startLinkedInAuth = () => {
    if (!isLinkedInOAuthConfigured) {
      onError('LinkedIn sign-in is not configured. Please contact support.');
      return;
    }

    const state = makeOAuthState();
    sessionStorage.setItem('linkedin_oauth_state', state);
    const params = new URLSearchParams({
      response_type: 'code',
      client_id: linkedinClientId,
      redirect_uri: linkedinRedirectUri,
      state,
      scope: linkedinScopes,
    });
    window.location.assign(`https://www.linkedin.com/oauth/v2/authorization?${params.toString()}`);
  };

  return (
    <button
      type="button"
      className="auth-linkedin"
      style={{
        width:'100%', padding:'11px 16px', background:'white', border:'1.5px solid #E5E7EB',
        borderRadius:8, fontSize:14, fontWeight:600, color:'#374151', cursor:'pointer',
        display:'flex', alignItems:'center', justifyContent:'center', gap:10,
        transition:'background 0.15s, border-color 0.15s', marginTop:10,
      }}
      onClick={startLinkedInAuth}
    >
      <LinkedInLogo />
      {isSignup ? 'Sign up with LinkedIn' : 'Sign in with LinkedIn'}
    </button>
  );
};

const SubmitBtn = ({ loading, disabled, children }) => (
  <button
    type="submit"
    className="auth-btn"
    disabled={loading || disabled}
    style={{
      width:'100%', padding:'13px', background:'linear-gradient(135deg,#B00D15,#EF1D28)',
      color:'white', border:'none', borderRadius:10, fontSize:15, fontWeight:700,
      cursor:'pointer', marginTop:4, transition:'transform 0.15s, box-shadow 0.15s',
      display:'flex', alignItems:'center', justifyContent:'center', gap:8,
    }}
  >
    {loading && (
      <span style={{ width:16, height:16, border:'2px solid rgba(255,255,255,0.35)', borderTopColor:'white', borderRadius:'50%', display:'inline-block', animation:'spin 0.7s linear infinite' }} />
    )}
    {children}
  </button>
);

/* ── error / notice banners ── */
const ErrorBanner = ({ msg }) => msg ? (
  <div style={{ background:'#FEF2F2', border:'1px solid #FECACA', color:'#DC2626', padding:'10px 14px', borderRadius:8, marginBottom:16, fontSize:13, display:'flex', alignItems:'center', gap:8 }}>
    <span style={{ fontSize:15 }}>⚠</span> {msg}
  </div>
) : null;

const NoticeBanner = ({ msg }) => msg ? (
  <div style={{ background:'#ECFDF5', border:'1px solid #6EE7B7', color:'#065F46', padding:'10px 14px', borderRadius:8, marginBottom:16, fontSize:13, display:'flex', alignItems:'center', gap:8 }}>
    <span style={{ fontSize:15 }}>✉</span> {msg}
  </div>
) : null;

/* ══════════════════════════════════
   LOGIN PAGE
══════════════════════════════════ */
const PROFILE_DASHBOARD_PATH = '/dashboard?view=profile';
const getPostAuthPath = (authData) => (
  authData?.isNewUser || authData?.profileAutofilled
    ? PROFILE_DASHBOARD_PATH
    : '/dashboard'
);

export const LoginPage = () => {
  const [email, setEmail]           = useState('');
  const [password, setPassword]     = useState('');
  const [showPwd, setShowPwd]       = useState(false);
  const [error, setError]           = useState('');
  const [loading, setLoading]       = useState(false);
  const [agreedToTerms, setAgreed]  = useState(false);
  const { login, googleLogin }      = useAuth();
  const navigate                    = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(''); setLoading(true);
    try {
      const data = await login(email, password);
      navigate(getPostAuthPath(data));
    }
    catch (err) { setError(err.message); }
    finally { setLoading(false); }
  };

  const handleGoogle = async (cr) => {
    setError('');
    try {
      const data = await googleLogin(cr.credential);
      navigate(getPostAuthPath(data));
    }
    catch (err) { setError(err.message); }
  };

  return (
    <>
      <AuthStyles />
      <div style={{ minHeight:'100vh', display:'flex', background:'white' }}>
        <BrandPanel isSignup={false} />

        {/* right panel */}
        <div style={{ flex:1, display:'flex', alignItems:'center', justifyContent:'center', padding:'32px 24px', minWidth:0 }}>
          <div className="auth-card" style={{ width:'100%', maxWidth:420 }}>

            {/* mobile logo */}
            <div style={{ textAlign:'center', marginBottom:28, display:'block' }} className="auth-mobile-logo">
              <div style={{ display:'inline-flex', alignItems:'center', gap:8, marginBottom:12 }}>
                <img src="/assets/mars-logo.png" alt="MarsResume" style={{ width:36, height:36, objectFit:'contain' }} />
                <span style={{ fontSize:20, fontWeight:900, background:'linear-gradient(135deg,#B00D15,#EF1D28)', WebkitBackgroundClip:'text', WebkitTextFillColor:'transparent' }}>MarsResume</span>
              </div>
            </div>

            <>
              <h1 style={{ margin:'0 0 4px', fontSize:26, fontWeight:800, color:'#111827', letterSpacing:'-0.4px' }}>Welcome back</h1>
              <p style={{ margin:'0 0 24px', color:'#6B7280', fontSize:14 }}>Sign in to continue building your career</p>

              <ErrorBanner msg={error} />

              <GoogleAuthButton onSuccess={handleGoogle} onError={() => setError('Google login failed')} isSignup={false} />
              <LinkedInAuthButton onError={setError} isSignup={false} />
              <Divider />

              <form onSubmit={handleSubmit}>
                <Field label="Email address" type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="you@example.com" required autoFocus />
                <Field
                  label="Password" type={showPwd ? 'text' : 'password'}
                  value={password} onChange={e => setPassword(e.target.value)}
                  placeholder="••••••••" required
                  rightEl={<span onClick={() => setShowPwd(p => !p)}><EyeIcon off={showPwd} /></span>}
                />

                <div style={{ textAlign:'right', marginTop:-8, marginBottom:18 }}>
                  <span style={{ fontSize:12, color:'#EF1D28', fontWeight:600, cursor:'pointer' }}>Forgot password?</span>
                </div>

                <TermsCheckbox checked={agreedToTerms} onChange={setAgreed} />
                <SubmitBtn loading={loading} disabled={!agreedToTerms}>{loading ? 'Signing in…' : 'Sign In'}</SubmitBtn>
              </form>
            </>


            {/* security badge */}
            <div style={{ display:'flex', alignItems:'center', justifyContent:'center', gap:6, marginTop:18, color:'#9CA3AF', fontSize:12 }}>
              <ShieldIcon /> <span>256-bit SSL encryption · Your data is safe</span>
            </div>

            <p style={{ textAlign:'center', marginTop:20, fontSize:13, color:'#6B7280' }}>
              Don&apos;t have an account?{' '}
              <Link to="/signup" style={{ color:'#EF1D28', fontWeight:700, textDecoration:'none' }}>Create one free →</Link>
            </p>
          </div>
        </div>
      </div>
    </>
  );
};

/* ══════════════════════════════════
   SIGNUP PAGE
══════════════════════════════════ */
export const SignupPage = () => {
  const [form, setForm]                     = useState({ name:'', email:'', password:'', confirm:'' });
  const [showPwd, setShowPwd]               = useState(false);
  const [showConfirm, setShowConfirm]       = useState(false);
  const [verificationSent, setVerSent]      = useState(false);
  const [code, setCode]                     = useState('');
  const [notice, setNotice]                 = useState('');
  const [error, setError]                   = useState('');
  const [loading, setLoading]               = useState(false);
  const [agreedToTerms, setAgreed]          = useState(false);
  const [referrerName, setReferrerName]     = useState('');
  const { requestSignupCode, signup, googleLogin } = useAuth();
  const navigate                            = useNavigate();
  const [searchParams]                      = useSearchParams();

  // Capture referral code from URL or localStorage on mount
  useEffect(() => {
    const refCode = searchParams.get('ref');
    if (refCode) {
      localStorage.setItem('mars_referral_code', refCode.toUpperCase());
      // Validate and get referrer name for display
      fetch(`${process.env.REACT_APP_API_URL || '/api'}/referral/validate/${refCode}`)
        .then(r => r.json())
        .then(d => { if (d.valid) setReferrerName(d.referrerName); })
        .catch(() => {});
    } else {
      const stored = localStorage.getItem('mars_referral_code');
      if (stored) {
        fetch(`${process.env.REACT_APP_API_URL || '/api'}/referral/validate/${stored}`)
          .then(r => r.json())
          .then(d => { if (d.valid) setReferrerName(d.referrerName); else localStorage.removeItem('mars_referral_code'); })
          .catch(() => {});
      }
    }
  }, [searchParams]);

  const set = k => e => setForm(f => ({ ...f, [k]: e.target.value }));

  const pwdStrength = (() => {
    const p = form.password;
    if (!p) return 0;
    let s = 0;
    if (p.length >= 8) s++;
    if (/[A-Z]/.test(p)) s++;
    if (/\d/.test(p)) s++;
    if (/[^a-zA-Z0-9]/.test(p)) s++;
    return s;
  })();
  const strengthLabel = ['','Weak','Fair','Good','Strong'][pwdStrength];
  const strengthColor = ['','#EF4444','#E06020','#EAB308','#22C55E'][pwdStrength];

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(''); setNotice('');
    if (!verificationSent) {
      if (form.password !== form.confirm) return setError('Passwords do not match');
      if (form.password.length < 6) return setError('Password must be at least 6 characters');
    } else {
      if (code.replace(/\s/g,'').length < 6) return setError('Enter the 6-digit verification code');
    }
    setLoading(true);
    try {
      if (!verificationSent) {
        await requestSignupCode(form.name, form.email, form.password);
        setVerSent(true);
        setNotice(`Check your inbox — we sent a code to ${form.email}`);
      } else {
        const data = await signup(form.name, form.email, form.password, code, undefined);
        navigate(getPostAuthPath(data));
      }
    } catch (err) { setError(err.message); }
    finally { setLoading(false); }
  };

  const handleResend = async () => {
    setError(''); setNotice(''); setLoading(true);
    try {
      await requestSignupCode(form.name, form.email, form.password);
      setNotice(`New code sent to ${form.email}`);
    } catch (err) { setError(err.message); }
    finally { setLoading(false); }
  };

  const handleGoogle = async (cr) => {
    setError('');
    try {
      const data = await googleLogin(cr.credential);
      navigate(getPostAuthPath(data));
    }
    catch (err) { setError(err.message); }
  };

  return (
    <>
      <AuthStyles />
      <div style={{ minHeight:'100vh', display:'flex', background:'white' }}>
        <BrandPanel isSignup />

        {/* right panel */}
        <div style={{ flex:1, display:'flex', alignItems:'center', justifyContent:'center', padding:'32px 24px', overflowY:'auto', minWidth:0 }}>
          <div className="auth-card" style={{ width:'100%', maxWidth:440, paddingBottom:16 }}>

            {/* mobile logo */}
            <div style={{ textAlign:'center', marginBottom:24 }}>
              <div style={{ display:'inline-flex', alignItems:'center', gap:8, marginBottom:10 }}>
                <img src="/assets/mars-logo.png" alt="MarsResume" style={{ width:34, height:34, objectFit:'contain' }} />
                <span style={{ fontSize:19, fontWeight:900, background:'linear-gradient(135deg,#B00D15,#EF1D28)', WebkitBackgroundClip:'text', WebkitTextFillColor:'transparent' }}>MarsResume</span>
              </div>
            </div>

            {/* Referral bonus banner */}
            {referrerName && (
              <div style={{ background:'linear-gradient(135deg,#ECFDF5,#D1FAE5)', border:'1.5px solid #6EE7B7', borderRadius:12, padding:'10px 14px', marginBottom:18, display:'flex', alignItems:'center', gap:10 }}>
                <span style={{ fontSize:20 }}>🎁</span>
                <div>
                  <div style={{ fontSize:13, fontWeight:700, color:'#065F46' }}>{referrerName} invited you!</div>
                  <div style={{ fontSize:12, color:'#047857' }}>Sign up now and get <strong>7 days of Pro free</strong></div>
                </div>
              </div>
            )}

            {/* step indicator */}
            <div style={{ display:'flex', alignItems:'center', justifyContent:'center', gap:0, marginBottom:24 }}>
              {['Details','Verify','Done'].map((s,i) => {
                const done = (i === 0 && verificationSent) || (i === 1 && false);
                const active = (i === 0 && !verificationSent) || (i === 1 && verificationSent);
                return (
                  <React.Fragment key={s}>
                    <div style={{ display:'flex', flexDirection:'column', alignItems:'center', gap:4 }}>
                      <div style={{
                        width:28, height:28, borderRadius:'50%', display:'flex', alignItems:'center', justifyContent:'center', fontSize:12, fontWeight:700,
                        background: done ? '#22C55E' : active ? 'linear-gradient(135deg,#B00D15,#EF1D28)' : '#E5E7EB',
                        color: (done || active) ? 'white' : '#9CA3AF',
                        transition:'all 0.3s',
                      }}>
                        {done ? <CheckIcon /> : i+1}
                      </div>
                      <span style={{ fontSize:10, fontWeight:600, color: active ? '#EF1D28' : '#9CA3AF' }}>{s}</span>
                    </div>
                    {i < 2 && <div style={{ width:48, height:2, background: done ? '#22C55E' : '#E5E7EB', marginBottom:16, transition:'background 0.3s' }} />}
                  </React.Fragment>
                );
              })}
            </div>

            {!verificationSent ? (
              <>
                <h1 style={{ margin:'0 0 4px', fontSize:24, fontWeight:800, color:'#111827', letterSpacing:'-0.4px' }}>Create your account</h1>
                <p style={{ margin:'0 0 20px', color:'#6B7280', fontSize:14 }}>Join thousands building better resumes with AI</p>

                <ErrorBanner msg={error} />

                <GoogleAuthButton onSuccess={handleGoogle} onError={() => setError('Google signup failed')} isSignup={true} />
                <LinkedInAuthButton onError={setError} isSignup={true} />
                <Divider />

                <form onSubmit={handleSubmit}>
                  <Field label="Full name" type="text" value={form.name} onChange={set('name')} placeholder="John Doe" required autoFocus />
                  <Field label="Email address" type="email" value={form.email} onChange={set('email')} placeholder="you@example.com" required />
                  <Field
                    label="Password" type={showPwd ? 'text' : 'password'}
                    value={form.password} onChange={set('password')}
                    placeholder="Min 6 characters" required
                    rightEl={<span onClick={() => setShowPwd(p => !p)}><EyeIcon off={showPwd} /></span>}
                  />

                  {/* password strength bar */}
                  {form.password && (
                    <div style={{ marginTop:-8, marginBottom:14 }}>
                      <div style={{ display:'flex', gap:4, marginBottom:4 }}>
                        {[1,2,3,4].map(n => (
                          <div key={n} style={{ flex:1, height:3, borderRadius:2, background: n <= pwdStrength ? strengthColor : '#E5E7EB', transition:'background 0.3s' }} />
                        ))}
                      </div>
                      <span style={{ fontSize:11, color:strengthColor, fontWeight:600 }}>{strengthLabel}</span>
                    </div>
                  )}

                  <Field
                    label="Confirm password" type={showConfirm ? 'text' : 'password'}
                    value={form.confirm} onChange={set('confirm')}
                    placeholder="Repeat password" required
                    rightEl={<span onClick={() => setShowConfirm(p => !p)}><EyeIcon off={showConfirm} /></span>}
                  />

                  <div style={{ background:'#F0FDF4', border:'1px solid #BBF7D0', borderRadius:8, padding:'10px 14px', marginBottom:16, fontSize:12, color:'#15803D', display:'flex', alignItems:'center', gap:8 }}>
                    <span>✅</span> Free plan includes 3 documents · No credit card required
                  </div>

                  <TermsCheckbox checked={agreedToTerms} onChange={setAgreed} />

                  <SubmitBtn loading={loading} disabled={!agreedToTerms}>{loading ? 'Sending code…' : 'Continue →'}</SubmitBtn>
                </form>
              </>
            ) : (
              <>
                <div style={{ textAlign:'center', marginBottom:8 }}>
                  <div style={{ width:56, height:56, borderRadius:'50%', background:'#ECFDF5', border:'2px solid #6EE7B7', display:'flex', alignItems:'center', justifyContent:'center', margin:'0 auto 16px', fontSize:24 }}>
                    ✉️
                  </div>
                  <h1 style={{ margin:'0 0 6px', fontSize:22, fontWeight:800, color:'#111827' }}>Check your email</h1>
                  <p style={{ margin:0, color:'#6B7280', fontSize:14, lineHeight:1.5 }}>
                    We sent a 6-digit code to<br />
                    <strong style={{ color:'#111827' }}>{form.email}</strong>
                  </p>
                </div>

                <ErrorBanner msg={error} />
                <NoticeBanner msg={notice} />

                <form onSubmit={handleSubmit}>
                  <label style={{ display:'block', fontSize:13, fontWeight:600, color:'#374151', marginBottom:8, textAlign:'center' }}>Enter verification code</label>
                  <OTPInput value={code} onChange={setCode} />

                  <SubmitBtn loading={loading}>{loading ? 'Verifying…' : 'Verify & Create Account'}</SubmitBtn>
                </form>

                <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginTop:16, padding:'12px 14px', background:'#F9FAFB', borderRadius:8 }}>
                  <button type="button" onClick={() => { setVerSent(false); setCode(''); setNotice(''); setError(''); }} style={{ background:'none', border:'none', color:'#6B7280', fontSize:12, fontWeight:600, cursor:'pointer', padding:0 }}>
                    ← Edit details
                  </button>
                  <button type="button" onClick={handleResend} disabled={loading} style={{ background:'none', border:'none', color:'#EF1D28', fontSize:12, fontWeight:700, cursor:'pointer', padding:0 }}>
                    Resend code
                  </button>
                </div>
              </>
            )}

            {/* security badge */}
            <div style={{ display:'flex', alignItems:'center', justifyContent:'center', gap:6, marginTop:16, color:'#9CA3AF', fontSize:12 }}>
              <ShieldIcon /> <span>256-bit SSL encryption · Your data is safe</span>
            </div>

            <p style={{ textAlign:'center', marginTop:16, fontSize:13, color:'#6B7280' }}>
              Already have an account?{' '}
              <Link to="/login" style={{ color:'#EF1D28', fontWeight:700, textDecoration:'none' }}>Sign in →</Link>
            </p>
          </div>
        </div>
      </div>
    </>
  );
};

export const LinkedInCallbackPage = () => {
  const [error, setError] = useState('');
  const [message, setMessage] = useState('Connecting to LinkedIn...');
  const hasHandled = useRef(false);
  const { linkedinLogin } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (hasHandled.current) return;
    hasHandled.current = true;

    const params = new URLSearchParams(window.location.search);
    const providerError = params.get('error_description') || params.get('error');
    const code = params.get('code');
    const state = params.get('state');
    const expectedState = sessionStorage.getItem('linkedin_oauth_state');
    sessionStorage.removeItem('linkedin_oauth_state');

    if (providerError) {
      setError(providerError);
      setMessage('');
      return;
    }

    if (!code) {
      setError('LinkedIn did not return an authorization code.');
      setMessage('');
      return;
    }

    if (!expectedState || expectedState !== state) {
      setError('LinkedIn sign-in could not be verified. Please try again.');
      setMessage('');
      return;
    }

    linkedinLogin(code, linkedinRedirectUri)
      .then(data => navigate(getPostAuthPath(data), { replace: true }))
      .catch(err => {
        setError(err.message);
        setMessage('');
      });
  }, [linkedinLogin, navigate]);

  return (
    <>
      <AuthStyles />
      <div style={{ minHeight:'100vh', background:'#F8F9FA', display:'flex', alignItems:'center', justifyContent:'center', padding:24 }}>
        <div className="auth-card" style={{ width:'100%', maxWidth:420, background:'white', border:'1px solid #E5E7EB', borderRadius:14, padding:28, textAlign:'center', boxShadow:'0 18px 50px rgba(15,23,42,0.08)' }}>
          <div style={{ display:'inline-flex', alignItems:'center', gap:8, marginBottom:18 }}>
            <img src="/assets/mars-logo.png" alt="MarsResume" style={{ width:34, height:34, objectFit:'contain' }} />
            <span style={{ fontSize:19, fontWeight:900, background:'linear-gradient(135deg,#B00D15,#EF1D28)', WebkitBackgroundClip:'text', WebkitTextFillColor:'transparent' }}>MarsResume</span>
          </div>

          {message && (
            <>
              <RocketLoader size={160} message={message} style={{ marginBottom: 8 }} />
              <p style={{ margin:0, color:'#6B7280', fontSize:14 }}>Please wait while we finish your sign-in.</p>
            </>
          )}

          {error && (
            <>
              <ErrorBanner msg={error} />
              <Link to="/login" style={{ display:'inline-flex', marginTop:4, color:'#EF1D28', fontWeight:700, textDecoration:'none', fontSize:14 }}>
                Back to sign in
              </Link>
            </>
          )}
        </div>
      </div>
    </>
  );
};
